import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff, Lock, User, ShieldCheck, Mail, RefreshCw } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { authService } from '../services/api'
import { useTheme } from '../context/ThemeContext'
import toast from 'react-hot-toast'

export default function Login() {
  const location = useLocation()
  const { login, loginWithToken } = useAuth()
  const { isDark } = useTheme()
  const navigate = useNavigate()
  const from = location.state?.from?.pathname || '/'

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 animate-fade-in relative">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src={isDark ? "/logo-w-tall.png" : "/logo-tall.png"}
              alt="Tony's Liquor Distributor"
              className="h-28 w-auto object-contain"
            />
          </div>
        </div>

        <div className="card p-6 md:p-8 backdrop-blur-sm bg-white/90 dark:bg-gray-900/80 border border-white/60 dark:border-white/5 shadow-2xl">
          <LoginForm login={login} loginWithToken={loginWithToken} navigate={navigate} from={from} />
          <p className="text-center text-xs text-gray-400 mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
            Don't have an account?{' '}
            <a href="mailto:orders@tonysliquor.co.za" className="text-primary hover:underline">Contact us to request access</a>
          </p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          By continuing you agree to our{' '}
          <Link to="/terms" className="hover:text-primary">Terms of Service</Link>{' '}
          and{' '}
          <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
        </p>
      </div>
    </div>
  )
}

function LoginForm({ login, loginWithToken, navigate, from }) {
  const [step, setStep]             = useState(1)       // 1 = creds, 2 = 2FA
  const [form, setForm]             = useState({ username: '', password: '' })
  const [twoFaMethod, setTwoFaMethod] = useState(null)  // 'totp' | 'email'
  const [emailHint, setEmailHint]   = useState('')
  const [otpCode, setOtpCode]       = useState('')
  const [showPw, setShowPw]         = useState(false)
  const [loading, setLoading]       = useState(false)
  const [resending, setResending]   = useState(false)

  // ── Step 1: credentials ────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.username || !form.password) { toast.error('Please fill in all fields'); return }
    setLoading(true)
    try {
      const result = await login(form.username, form.password)
      if (result.requires2fa) {
        setTwoFaMethod(result.twoFaMethod || 'totp')
        setEmailHint(result.emailHint || '')
        setStep(2)
        setOtpCode('')
        if (result.twoFaMethod === 'email') {
          toast.success('A verification code has been sent to your email.')
        }
      } else {
        toast.success('Welcome back!')
        navigate(from, { replace: true })
      }
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Invalid username or password')
    } finally {
      setLoading(false)
    }
  }

  // ── Step 2a: TOTP verify ───────────────────────────────────────
  const handleTotp = async (e) => {
    e.preventDefault()
    const clean = otpCode.replace(/\s/g, '')
    if (clean.length !== 6) { toast.error('Please enter the 6-digit code'); return }
    setLoading(true)
    try {
      const res = await authService.login2fa(form.username, form.password, clean)
      await loginWithToken(res.access_token)
      toast.success('Welcome back!')
      navigate(from, { replace: true })
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Invalid authenticator code')
    } finally {
      setLoading(false)
    }
  }

  // ── Step 2b: Email OTP verify ──────────────────────────────────
  const handleEmailOtp = async (e) => {
    e.preventDefault()
    const clean = otpCode.replace(/\s/g, '')
    if (clean.length !== 6) { toast.error('Please enter the 6-digit code'); return }
    setLoading(true)
    try {
      const res = await authService.loginEmailOtpVerify(form.username, form.password, clean)
      await loginWithToken(res.access_token)
      toast.success('Welcome back!')
      navigate(from, { replace: true })
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Invalid or expired code')
    } finally {
      setLoading(false)
    }
  }

  // ── Resend email OTP ───────────────────────────────────────────
  const handleResend = async () => {
    setResending(true)
    try {
      await authService.loginEmailOtpSend(form.username, form.password)
      toast.success('A new code has been sent to your email.')
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to resend code')
    } finally {
      setResending(false)
    }
  }

  // ── 2FA step ───────────────────────────────────────────────────
  if (step === 2) {
    const isEmail = twoFaMethod === 'email'
    return (
      <form onSubmit={isEmail ? handleEmailOtp : handleTotp} className="space-y-5">
        <div className="text-center pb-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 border border-primary/30 mb-3">
            {isEmail
              ? <Mail size={26} className="text-primary" />
              : <ShieldCheck size={26} className="text-primary" />}
          </div>
          <h2 className="text-gray-900 dark:text-white font-display font-semibold text-lg">
            Two-Factor Verification
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1.5 leading-relaxed">
            {isEmail
              ? <>A 6-digit code was sent to{' '}
                  <strong className="text-gray-700 dark:text-gray-300">{emailHint || 'your email'}</strong>.
                  Enter it below.</>
              : 'Enter the 6-digit code from your authenticator app.'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-display font-medium text-gray-700 dark:text-gray-200 mb-1.5">
            {isEmail ? 'Email Code' : 'Authenticator Code'}
          </label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otpCode}
            onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="input-field text-center text-3xl tracking-[0.5em] font-mono"
            autoComplete="one-time-code"
            autoFocus
          />
        </div>

        <button type="submit" disabled={loading || otpCode.length < 6} className="btn-primary w-full py-3">
          {loading ? 'Verifying…' : 'Verify & Sign In'}
        </button>

        {isEmail && (
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="w-full flex items-center justify-center gap-1.5 text-sm text-gray-400 hover:text-primary transition-colors py-1"
          >
            <RefreshCw size={13} className={resending ? 'animate-spin' : ''} />
            {resending ? 'Sending…' : "Didn't receive it? Resend code"}
          </button>
        )}

        <button
          type="button"
          onClick={() => { setStep(1); setOtpCode(''); setTwoFaMethod(null) }}
          className="w-full text-center text-sm text-gray-400 hover:text-primary transition-colors"
        >
          ← Back to login
        </button>
      </form>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-display font-medium text-gray-700 dark:text-gray-200 mb-1.5">
          Username or Email
        </label>
        <div className="relative">
          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={form.username}
            onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
            placeholder="Your username"
            className="input-field pl-9"
            autoComplete="username"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-display font-medium text-gray-700 dark:text-gray-200 mb-1.5">Password</label>
        <div className="relative">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type={showPw ? 'text' : 'password'}
            value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            placeholder="Your password"
            className="input-field pl-9 pr-10"
            autoComplete="current-password"
          />
          <button type="button" onClick={() => setShowPw(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <div className="text-right mt-1.5">
          <Link to="/forgot-password" className="text-xs text-primary hover:underline">Forgot password?</Link>
        </div>
      </div>

      <button type="submit" disabled={loading} className="btn-primary w-full py-3 mt-2">
        {loading ? 'Signing in…' : 'Sign In'}
      </button>
    </form>
  )
}
